import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import RoundedTable from "../../components/table-component/RoundedTable";
import { TABLE_CELL_TYPES } from "../../components/table-component/RoundedTable";
import ErrorBox from "../../components/ErrorBox";
import CustomButton from "../../components/CustomButton";
import addIcon from "../../assets/add_icon.png";
import deleteIcon from "../../assets/delete_Icon.png";
import axiosClient from "../../services/axiosClient";
import AreYouSureModal from "../../components/AreYouSureModal";
import InformationBox from "../../components/InformationBox";
import AddEmployeeDeductionModal from "../school/AddEmployeeDeductionModal";

const DeductionsCardContent = ({ employeeId }) => {
  const { t } = useTranslation("general");

  const [isFetchingDeductions, setIsFetchingDeductions] = useState(false);
  const [fetchingDeductionsError, setFetchingDeductionsError] = useState(null);
  const [userDeductions, setUserDeductions] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const tableTitles = [
    { value: "deduction", keepOnMobile: true },
    { value: "date", keepOnMobile: true },
    { value: "deductionDate", keepOnMobile: true },
    { value: "amount", keepOnMobile: true },
    { value: "notes", keepOnMobile: true },
  ];

  // Data fetching
  const getAllDeductionsData = async (page) => {
    try {
      setIsFetchingDeductions(true);
      setFetchingDeductionsError(null);

      const response = await axiosClient.get(
        "/userDeductions/getUserDeductions",
        {
          params: {
            page,
            userId: employeeId,
          },
        }
      );

      setUserDeductions(response.data.userDeductions);
      setPagination({
        currentPage: response.data.currentPage,
        totalPages: response?.data?.totalPages,
        totalRecords: response?.data?.totalUserDeductions,
      });
    } catch (error) {
      console.error(
        "Error fetching deductions:",
        error.response?.data || error
      );
      setFetchingDeductionsError("errorFetchingDeductions");
    } finally {
      setIsFetchingDeductions(false);
    }
  };

  const tableRows = userDeductions.map((userDeduction) => [
    {
      value: userDeduction?.deduction?.name,
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
    {
      value: userDeduction?.date,
      type: TABLE_CELL_TYPES.DATE,
      keepOnMobile: true,
    },
    {
      value: userDeduction?.deductionDate,
      type: TABLE_CELL_TYPES.DATE,
      keepOnMobile: true,
    },
    {
      value: userDeduction?.deduction?.price,
      type: TABLE_CELL_TYPES.MONEY,
      keepOnMobile: true,
      currency: "IQD",
    },
    {
      value: userDeduction?.notes || t("noNotes"),
      type: TABLE_CELL_TYPES.TEXT,
      keepOnMobile: true,
    },
  ]);
  // Effects
  useEffect(() => {
    getAllDeductionsData(pagination.currentPage);
  }, [pagination.currentPage, employeeId]);

  return (
    <div className="o-column o-column--gap-size_300">
      <div className="o-row o-row--horizontal-align_end o-row--gap-size_500">
        {selectedRows.length > 0 && (
          <CustomButton
            title={t("delete")}
            icon={deleteIcon}
            iconSize={20}
            variant={"red"}
            onClick={() => setShowDeleteModal(true)}
          />
        )}
        <CustomButton
          onClick={() => setShowAddModal(true)}
          title={t("addDeduction")}
          variant="blank"
          icon={addIcon}
          iconSize={20}
        />
      </div>

      {fetchingDeductionsError && (
        <ErrorBox>{t(fetchingDeductionsError)}</ErrorBox>
      )}

      {isFetchingDeductions && <Loader size={LOADER_SIZES.MEDIUM} />}

      {!fetchingDeductionsError &&
        !isFetchingDeductions &&
        userDeductions.length === 0 && (
          <InformationBox>{t("noDeductionsFound")}</InformationBox>
        )}

      {!fetchingDeductionsError &&
        !isFetchingDeductions &&
        userDeductions.length > 0 && (
          <RoundedTable
            titles={tableTitles}
            rows={tableRows}
            pagination={pagination}
            setPagination={setPagination}
            withCheckbox
            fullData={userDeductions}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
          />
        )}

      {showDeleteModal && (
        <DeleteModal
          setShowDeleteModal={setShowDeleteModal}
          selectedRows={selectedRows}
          onSuccess={() => {
            setShowDeleteModal(false);
            getAllDeductionsData(pagination.currentPage);
            setSelectedRows([]);
          }}
        />
      )}

      {showAddModal && (
        <AddEmployeeDeductionModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            getAllDeductionsData(pagination.currentPage);
            setSelectedRows([]);
          }}
          employeeId={employeeId}
        />
      )}
    </div>
  );
};

function DeleteModal({ setShowDeleteModal, selectedRows, onSuccess }) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingError, setDeletingError] = useState(null);
  const { t } = useTranslation("general");

  const handleDelete = async () => {
    try {
      setIsDeleting(true);

      // TODO: Replace with actual API call when endpoint is ready
      await axiosClient.delete("/userDeductions/deleteMultipleUserDeductions", {
        data: {
          ids: selectedRows.map((row) => row.id),
        },
      });

      onSuccess();
    } catch (error) {
      console.error(
        "Error deleting deductions:",
        error.response?.data || error
      );
      setDeletingError("errorDeletingDeductions");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AreYouSureModal
      onClose={() => setShowDeleteModal(false)}
      onConfirm={handleDelete}
      customMessage={t("areYouSureToDeleteDeductions")}
      closeTitle="cancel"
      confirmTitle="delete"
      loading={isDeleting}
      confirmButtonVariant="red"
      error={t(deletingError)}
    />
  );
}

export default DeductionsCardContent;
